import { durationToHours } from './../../../utils/durationToHours'
import moment from 'moment';
import { createSelector } from 'reselect';
import { RootState } from '../../../store/rootState';
import { statusColor } from '../../../utils/statusColor';
import { daysDiff } from '..//../../utils/daysDiff';
import { sortTasks } from '../../../utils/sort';
import { IActivityFilter } from '../types/activityFilters';
import { IProjectActivity, IProjectActivityClustering, TaskStatus } from '../types/projectActivity';
import { IPagination } from '../../../types/pagination';

export const timeCompletion = (start_date: string | number | Date | null, end_date: string | number | Date | null): number => {
  if (start_date && end_date) {
    const totalDuration = daysDiff(new Date(start_date), new Date(end_date))
    const currentUsed = daysDiff(new Date(start_date), new Date())
    if (currentUsed > 0) {
      const compl = (currentUsed / totalDuration) * 100
      return Math.round(compl)
    }
    return 0
  }

  return 0
}

const calcHealth = (act: IProjectActivity) => {
  let percentage = 0
  if (act.budget && +act.budget !== 0 && act.forecast && +act.forecast !== 0) {
    percentage = act.forecast / act.budget
  }
  const health = percentage > 1.05 ? 'off_track' : percentage > .95 ? 'at_risk' : 'on_track'
  return health
}

export const remainingTime = (current_hours: number, total_hours: number): string[] => {
  const _remaining = `${(total_hours - current_hours)}`
  const _remainingPercentage = `${(100 - (current_hours / total_hours) * 100).toFixed(0)}%`
  if (_remainingPercentage === 'NaN%' || _remainingPercentage === '-Infinity%') {
    return [_remaining, '-']
  }
  return [`${durationToHours(+_remaining)} h`, _remainingPercentage]
}

const getFilterCount = (state: RootState) => state.projectsState.actFilters
const getSortClustering = (state: RootState) => state.projectsState.sortActs

export const getActivitiesState = (state: RootState): IProjectActivity[] => state.projectsState.activities
export const getPaginatedActivitiesState = (state: RootState): IPagination<IProjectActivity[]> => state.projectsState.paginatedActivities

const getSearchActivities = (state: RootState) => state.projectsState.searchActivities

const mapActs = (activities: IProjectActivity[]): IProjectActivity[] => {
  return activities.map(act => {
    return {
      ...act,
      current_hours: +act.current_hours,
      total_hours: +act.total_hours,
      forecast: act.forecast ? +act.forecast : 0,
      forecast_hours: act.forecast_hours ? +act.forecast_hours : 0,
      statusColor: (status: string) => statusColor(status),
      remainingHours: remainingTime(act.current_hours, act.total_hours)[0],
      remainingHoursPercentage: remainingTime(act.current_hours, act.total_hours)[1],
      expenditures: act.expenditures ? parseFloat(act.expenditures.toString()) : 0
    }
  })
}

export const getAllActSelector = createSelector(
  getActivitiesState,
  (activities) => {
    return mapActs(activities)
  }
)

export const getActivitiesSelector = createSelector(
  [getActivitiesState, getSearchActivities],
  (activities, getSearchActivities) => {
    if (getSearchActivities.search !== '') {
      return mapActs(activities.filter(
        a => a.name.toLowerCase().includes(getSearchActivities.search.toLowerCase())
      ))
    }
    return mapActs(activities)
  }
)

export const getActivitesInDanger = createSelector(
  [getActivitiesState],
  (activities) => {
    const acts = activities.map(act => {
      return {
        ...act,
        health: calcHealth(act),
        current_hours: +act.current_hours,
        total_hours: +act.total_hours,
        forecast_hours: act.forecast_hours ? +act.forecast_hours : 0,
        estimate_overdue: act.estimate_overdue ? +act.estimate_overdue : 0,
        statusColor: (status: string) => statusColor(status),
        remainingHours: remainingTime(act.current_hours, act.total_hours)[0],
        remainingHoursPercentage: remainingTime(act.current_hours, act.total_hours)[1]
      }
    })
    return acts.filter(act =>
      act.status === 'in_progress'
      && (act.estimate_overdue
      && act.current_hours
      && act.estimate_overdue + act.current_hours > act.total_hours
      || act.forecast_hours > act.total_hours) )
  }
)

export const getPaginatedActivitesInDanger = createSelector(
  [getPaginatedActivitiesState],
  (activities) => {
    const acts = activities.results.map(act => {
      return {
        ...act,
        health: calcHealth(act),
        current_hours: +act.current_hours,
        total_hours: +act.total_hours,
        forecast_hours: act.forecast_hours ? +act.forecast_hours : 0,
        estimate_overdue: act.estimate_overdue ? +act.estimate_overdue : 0,
        statusColor: (status: string) => statusColor(status),
        remainingHours: remainingTime(act.current_hours, act.total_hours)[0],
        remainingHoursPercentage: remainingTime(act.current_hours, act.total_hours)[1]
      }
    })
    return {
      ...activities,
      results: acts
    }
  }
)

export const getFilterCountSelector = createSelector(
  getFilterCount,
  filters => {
    const count = filters.map(f => f.options)
    const _filters = count.flat().filter(o => o.selected)
    return _filters.length
  }
)

const flattenFilters = (type: string, filters: IActivityFilter[]) => {
  return filters
    .filter(f => f.name === type)
    .map(f => f.options.map((opt) => opt))
    .flat()
    .filter(o => o.selected)
    .map(o => o.value)
}

export const getFilteredActivities = createSelector(
  getFilterCount,
  getActivitiesSelector,
  getFilterCountSelector,
  (actFilters, activities, filterCount) => {
    const statusFilters = flattenFilters('Status', actFilters)
    const dueDateFilters = flattenFilters('Due Date', actFilters)
    /* #### Not in use until discuss with cli.
    const membersFilters = flattenFilters('Members', actFilters)
     #### */
    const membersFilters = flattenFilters('Members', actFilters)
    const departmentFilters = flattenFilters('Departments', actFilters)
    if (filterCount > 0) {
      const _byDepartment = activities.filter(a => {
        if (departmentFilters.includes(a.department.id.toString())) {
          return a
        }
      })
      const _byMember = (departmentFilters.length > 0 ? _byDepartment : activities).filter(a => {
        if (membersFilters.some((item) => a.booked_employees?.map(b => +b.id).includes(+item))) {
          return a
        }
      })
      const _byStatus = (departmentFilters.length > 0 ? _byDepartment : membersFilters.length > 0 ? _byMember : activities).filter(a => {
        if (statusFilters.includes(a.status)) {
          return a
        }
      })
      const _byDueDate = (statusFilters.length > 0 ? _byStatus : membersFilters.length > 0 ? _byMember : departmentFilters.length > 0 ? _byDepartment : activities).filter(a => {
        if (dueDateFilters.length > 0) {
          if (a.end_date) {
            const _obj_end_date = moment(new Date(a.end_date)).format('YYYY-MM-DD')
            const _todayDate = moment(new Date()).format('YYYY-MM-DD')
            if (dueDateFilters.includes('individual')) {
              const activeIndividual = actFilters
                .filter(f => f.name === 'Due Date')
                .map(f => f.options.map((opt) => opt))
                .flat()
                .filter(o => o.selected)
                .map(o => o)
              const _start_date = activeIndividual[0] && activeIndividual[0].start_date !== undefined ? moment(new Date(activeIndividual[0].start_date)).format('YYYY-MM-DD') : null
              const _end_date = activeIndividual[0] && activeIndividual[0].end_date !== undefined ? moment(new Date(activeIndividual[0].end_date)).format('YYYY-MM-DD') : null
              if (_start_date !== null && _end_date !== null) {
                if (moment(_obj_end_date).isSameOrBefore(_end_date) && moment(_obj_end_date).isSameOrAfter(_start_date)) {
                  return a
                }
              }
            }
            if (dueDateFilters.includes('today') && moment(_obj_end_date).isSame(_todayDate)) {
              return a
            }

            if (dueDateFilters.includes('this_week') && moment(_todayDate).isSame(_obj_end_date, 'year')
              && moment(_todayDate).isSame(_obj_end_date, 'week')) {
              return a
            }

            if (dueDateFilters.includes('this_month') && moment(_todayDate).isSame(_obj_end_date, 'month')) {
              return a
            }

            if (dueDateFilters.includes('overdue') && (a.status === 'in_progress' || a.status === 'upcoming') && moment(_obj_end_date).isBefore(_todayDate)) {
              return a
            }

          }
        } else {
          return a
        }
      })
      return [..._byDueDate]
    }
    return sortTasks(activities, "name")
  }
)

const statusClustering = (activities: IProjectActivity[]): IProjectActivityClustering[] => {
  const status: TaskStatus[] = [['upcoming', 'Upcoming'], ['completed', 'Completed'], ['in_progress', 'In Progress']]
  const clustered: IProjectActivityClustering[] = status.map(s => {
    return {
      name: s[1],
      activities: activities.filter(a => a.status === s[0])
    }
  })

  return clustered
}

const departmentClustering = (activities: IProjectActivity[]): IProjectActivityClustering[] => {
  const deps = activities.map(a => a.department).filter(
    (_dep, idx, _deps) => idx === _deps.findIndex(_d => _d?.id === _dep?.id) && _dep
  )
  if (deps.length !== 0) {
    const clustered: IProjectActivityClustering[] = deps.sort((a, b) => a.name.localeCompare(b.name)).map(dep => {
      return {
        name: dep.name,
        activities: activities.filter(a => +a.department.id === +dep.id)
      }
    }).filter(_d => _d.activities.length !== 0)

    return clustered
  }
  return []
}

const phaseClustering = (activities: IProjectActivity[]): IProjectActivityClustering[] => {
  const phases = activities.map(a => a.phase)
    .filter((_phase, idx, _phases) => idx === _phases.findIndex(_p => _p.id === _phase.id))
    .sort((a, b) => {
      const aStartDate = new Date(a.start_date).getTime()
      const bStartDate = new Date(b.start_date).getTime()
      const aEndDate = new Date(a.end_date).getTime()
      const bEndDate = new Date(b.end_date).getTime()
      return aStartDate !== bStartDate ?
        aStartDate - bStartDate
        : aEndDate - bEndDate
    }
  )
  const clustered: IProjectActivityClustering[] = phases.map(phase => {
    return {
      name: phase.name,
      phase: phase,
      activities: activities.filter(a => +a.phase.id === +phase.id)
    }
  }).filter(_d => _d.activities.length !== 0)

  return clustered
}

export const getClusteredActivitiesSelector = createSelector(
  [getFilteredActivities,
  getSortClustering],
  (activities, sortClustering) => {
    if (activities) {
      if (sortClustering.clustering === 'completion') {
        return statusClustering(activities)
      }

      if (sortClustering.clustering === 'department') {
        return departmentClustering(activities)
      }

      if (sortClustering.clustering === 'phase') {
        return phaseClustering(activities.filter(a => a.phase))
      }

      if (sortClustering.clustering === 'overall') {
        return [{ name: 'Overall', activities: activities }]
      }
    }
  }
)
